import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/existingPatient/page.module.scss';
import Typography from '@mui/material/Typography';
import { useDispatch } from 'react-redux';
import { actionTypes } from 'reducer/actionType';
import { usePersistExistingPatientReducer } from 'reducer/existingUser';
import { HpiFromIntakeElementType } from 'types/hpi';
import Spinner from 'components/spinner/Spinner';
import { clearLocalStorage, parseErrorMessage } from 'utils/helper';
import axios from 'axios';
import { StateTypeLocalStorage } from 'reducer/medicalHistory';
import MetaTag from 'components/meta';
import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import dayjs from 'dayjs';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import { useAddReasonForVisit } from 'hooks/useApi';
import useIncompleteTempoRedirect from 'hooks/useIncompleteTempoRedirect';
import useCommuniqueState from 'hooks/useCommuniqueState';
import { appointmentIntakeStatusAction } from 'reduxStateSlice/appointment';

const title = 'You are done!';

function PageComplete() {
  const dispatch = useDispatch();
  const { setCommuniqueData, communiqueData } = useCommuniqueState();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const { mutateAsync: addReasonForVisit } = useAddReasonForVisit();

  useIncompleteTempoRedirect(
    !isLoading
    && errorMessage === ''
    && !communiqueData?.sendForms?.medicalHistory,
  );

  const [state] = usePersistExistingPatientReducer();

  const sendFollowUpData = useCallback(async () => {
    const stillExperiencing = state.find(
      (p: StateTypeLocalStorage) => p.name === actionTypes.PAGE_ONE,
    );
    const updatePainLevel = state.find(
      (p: StateTypeLocalStorage) => p.name === actionTypes.PAGE_TWO,
    );
    const compliantWith = state.find(
      (p: StateTypeLocalStorage) => p.name === actionTypes.PAGE_THREE,
    );

    const stillExperiencingArray: string[] = [];
    const compliantWithArray: string[] = [];

    forEach(stillExperiencing.value, (item) => {
      stillExperiencingArray.push(item.name, item.value);
    });

    forEach(compliantWith.value, (item) => {
      compliantWithArray.push(item.name, item.value);
    });

    const hpiPayload: HpiFromIntakeElementType[] = [
      {
        title: 'Updated Pain Level',
        hpiAttributes: [updatePainLevel.value],
      },
      {
        title: 'Compliant with',
        hpiAttributes: compliantWithArray,
      },
    ];

    if (stillExperiencingArray.length) {
      hpiPayload.unshift({
        title: 'Still Experiencing',
        hpiAttributes: stillExperiencingArray,
      });
    }

    const payload = {
      encounterId: communiqueData?.appointmentId ?? 0,
      visitDateTimeOffset: dayjs().format(),
      hpiFromIntake: {
        hpiFromIntakeId: 0,
        visitNoteBaseId: 0,
        hpiFromIntakeElements: hpiPayload,
      },
    };
    try {
      await addReasonForVisit(payload);
      setIsLoading(false);
      if (!isNil(communiqueData)) {
        setCommuniqueData({
          ...communiqueData,
          sendForms: {
            ...communiqueData?.sendForms,
            isFollowUp: false,
          },
        });
      }
      dispatch(appointmentIntakeStatusAction({ hpiSubmitted: true }));

      if (communiqueData?.sendForms?.medicalHistory) {
        dispatch(
          appStateSettingsAction({
            name: ModuleTypes.medicalHistory,
            page: null,
          }),
        );
      } else {
        clearLocalStorage();
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`${parseErrorMessage(error)}`);
      } else {
        setErrorMessage(
          'Something went wrong on AddReasonForVisit for follow-up',
        );
      }
      setIsLoading(false);
      console.error('🚀 ~ error on Submitting AddReasonForVisit : ', error);
    }
  }, []);

  useEffect(() => {
    sendFollowUpData();
  }, []);

  // eslint-disable-next-line no-nested-ternary
  const titleText = isLoading
    ? 'Submitting follow up reason for visit...'
    : errorMessage === ''
      ? title
      : errorMessage;

  return (
    <>
      <MetaTag
        title={titleText}
      />
      <Box pb={1}>
        <Box
          className={`marginTopZero ${styles.titleText}`}
          minHeight="90vh"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          {!isLoading && errorMessage === '' ? (
            <>
              <Typography sx={{ fontSize: 40, mb: 2, fontWeight: 600 }}>
                {' '}
                Congratulations!
              </Typography>
              <Typography className={`marginTopZero ${styles.titleText}`}>
                {title}
              </Typography>
            </>
          ) : (
            isLoading && <Spinner />
          )}
          {errorMessage && (
            <p className="error-message w-full">{errorMessage}</p>
          )}
        </Box>
      </Box>
    </>
  );
}

export default PageComplete;
